import {
  AfterViewInit,
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { MatPaginator, PageEvent } from '@angular/material/paginator';

import { CurrencyUomService } from 'src/app/core/services/currency-uom.service';
import { FiltersService } from 'src/app/core/services/filters.service';
import { UIPreferencesService } from 'src/app/core/services/ui-preferences.service';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { TVDService } from '../../services/tvd.service';
import {
  TvdByProgramAggregatedRow,
  TvdByProgramData,
  TvdImpactCategory,
} from '../../types/tvd-types';
import { Region } from 'src/app/core/services/ebr-api-client/ebr-api-client.g';
import { FiltersStore } from 'src/app/core/stores/filters.store';
import { regionDisplayNames } from 'src/app/global/types';
import { TvdProgramsStore } from '../../stores/tvd-programs.store';

type TvdProgramMetric = TvdByProgramData['programMetrics'][number];

@Component({
    selector: 'tvd-program-table',
    templateUrl: './tvd-program-table.component.html',
    styleUrls: ['./tvd-program-table.component.scss'],
    standalone: false
})
export class TvdProgramTableComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  filters: ContractModel | undefined;
  private _allProgramRows: TvdByProgramData[] = []; // Unfiltered, unaggregated data from the API
  tableRows: TvdByProgramAggregatedRow[] = []; // Aggregated data for display based on current filters

  // Pagination State
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  pagedRows: TvdByProgramAggregatedRow[] = [];
  pageIndex = 0;
  pageSize = 10;

  // Row actions state
  openActionsProgram: string | null = null;

  // Filters
  filteredProgramRows: TvdByProgramData[] = [];
  programSearchTerm = '';
  availableRegions: Region[] = [];
  availableImpactCategories: TvdImpactCategory[] = [];
  selectedRegions: Region[] = [];
  selectedImpactCategories: TvdImpactCategory[] = [];

  // UI State and Preferences
  isLoading = false;
  displayInRoundedNumber = true;
  currencyCode = 'USD';

  // Subscriptions
  private _displayedCurrencyCodeSub?: Subscription;
  private _displayInRoundedNumberSub?: Subscription;
  private _filtersSub?: Subscription;
  private _currencyUomSub?: Subscription;
  private _programsCacheInvalidatedSub?: Subscription;

  constructor(
    private _filtersService: FiltersService,
    private _currencyUomService: CurrencyUomService,
    private _uiPreferencesService: UIPreferencesService,
    private _filtersStore: FiltersStore,
    private _tvdProgramsStore: TvdProgramsStore,
  ) {}

  async ngOnInit(): Promise<void> {
    this._filtersSub = this._filtersService.selectedFiltersObservable.subscribe(
      (f) => (this.filters = f),
    );

    this._displayInRoundedNumberSub =
      this._uiPreferencesService.displayRoundedValuesObservable.subscribe(
        (f) => (this.displayInRoundedNumber = f),
      );

    this._displayedCurrencyCodeSub =
      this._currencyUomService.displayedCurrency$.subscribe(() => {
        this.currencyCode = this._currencyUomService.getDisplayedCurrency();
      });

    // Subscribe to cache invalidation triggers from the store to refresh data when filters or currency/UOM change
    this._programsCacheInvalidatedSub =
      this._tvdProgramsStore.cacheInvalidated$.subscribe(async () => {
        this._currencyUomService.setCurrencyLoading(true);
        try {
          await this.initializeProgramTable();
        } finally {
          this._currencyUomService.setCurrencyLoading(false);
        }
      });

    // Initial load
    if (this.filters) {
      await this.initializeProgramTable();
    }
  }

  ngAfterViewInit(): void {
    // Use setTimeout instead of requestAnimationFrame to avoid racing with Router scroll restoration on NavigationEnd
    if (this._filtersStore.oneCustomerNameValue !== '') {
      setTimeout(() => this.scrollIntoViewIfDrilledIn());
    }
  }

  ngOnDestroy(): void {
    // Save current filter state to store for restoration on breadcrumb navigation back to this table
    this._tvdProgramsStore.saveProgramTableFilterState({
      programSearchTerm: this.programSearchTerm,
      selectedRegions: this.selectedRegions,
      selectedImpactCategories: this.selectedImpactCategories,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
    });
    this._filtersSub?.unsubscribe();
    this._displayInRoundedNumberSub?.unsubscribe();
    this._displayedCurrencyCodeSub?.unsubscribe();
    this._currencyUomSub?.unsubscribe();
    this._programsCacheInvalidatedSub?.unsubscribe();
  }

  /**
   * Initializes the program table by loading data from the store (or API if not cached),
   * processing it for display, and setting up the initial filter options.
   * This method should be called on component initialization and whenever the underlying data needs to be reloaded (e.g., after a currency change).
   */
  async initializeProgramTable(): Promise<void> {
    this.isLoading = true;
    try {
      // Load data from store (will use cache if available, otherwise fetch from API)
      await this._tvdProgramsStore.loadTvdProgramsData();
      const data = this._tvdProgramsStore.getProgramsDataValue();

      this._allProgramRows = data.map((program) => ({
        ...program,
        programMetrics: (program.programMetrics ?? []).map((metric) => ({
          ...metric,
          impactCategories: this.parseImpactCategories(
            (metric.impactCategories as unknown as string[]) ?? [],
          ),
        })),
      }));

      this.filteredProgramRows = this._allProgramRows; // Initially, no filters are applied

      this.initializeFilterOptions();

      const filtersWereRestored = this.restoreFilterState();
      if (!filtersWereRestored) {
        // No saved state — show all rows with default pagination
        this.tableRows = this.aggregateTvdByProgramData(this._allProgramRows);
        this.updatePagedRows();
      }
    } finally {
      this.isLoading = false;
    }
  }

  private restoreFilterState(): boolean {
    const savedFilters = this._tvdProgramsStore.getProgramTableFilterState();
    if (!savedFilters) {
      return false;
    }
    this.programSearchTerm = savedFilters.programSearchTerm;
    this.selectedRegions = savedFilters.selectedRegions;
    this.selectedImpactCategories = savedFilters.selectedImpactCategories;
    this.pageIndex = savedFilters.pageIndex;
    this.pageSize = savedFilters.pageSize;
    this.applyAllFilters(false); // preserve restored pageIndex
    return true;
  }

  /**
   * Aggregates the raw program data from the API into a format suitable for display in the table, summing up totals and combining impact categories across all regions for each program.
   * This method should be called whenever the underlying program data changes (e.g., after filtering) to ensure the displayed totals and categories are accurate.
   * @param rows The raw program data retrieved from the API.
   * @returns An array of aggregated program data suitable for display in the table.
   */
  aggregateTvdByProgramData(
    rows: TvdByProgramData[],
  ): TvdByProgramAggregatedRow[] {
    return rows
      .map((programRow) => {
        const regions = programRow.programMetrics;

        const valueDeliveredTotal = regions.reduce(
          (s, r) => s + r.totalValueDelivered.value,
          0,
        );

        return {
          program: programRow.program,

          totalProducts: regions.reduce((s, r) => s + r.totalProducts, 0),

          totalLocations: regions.reduce((s, r) => s + r.totalLocations, 0),

          totalValueDelivered: {
            value: valueDeliveredTotal,
            currencyCodeId:
              regions[0]?.totalValueDelivered.currencyCodeId ?? '',
          },

          impactCategories: [
            ...new Set(
              regions.flatMap((r) => r.impactCategories as TvdImpactCategory[]),
            ),
          ],
        };
      })
      .sort(
        (a, b) => b.totalValueDelivered.value - a.totalValueDelivered.value,
      );
  }

  // #region Filter Management
  /**
   * Applies all active filters (search term, region, impact category) to the base program data, then re-aggregates and paginates the results for display.
   * This method should be called whenever any filter criteria changes to ensure the displayed table data is up to date.
   * @param resetPage Whether to reset the pagination back to the first page after applying filters. This should typically be true when filters change, but can be false when re-applying filters during initialization to preserve the restored page index.
   */
  applyAllFilters(resetPage = true): void {
    this.updateAvailableFilterOptions();

    let rows = [...this._allProgramRows];

    if (this.programSearchTerm.trim()) {
      const term = this.programSearchTerm.toLowerCase();
      rows = rows.filter((row) => row.program.toLowerCase().includes(term));
    }

    // The region filter is a "hard" filter which aggregates only the selected regions for each program
    // and updates the locations, products, impact categories and value delivered.
    if (this.hasSelectedRegions()) {
      rows = rows
        .map((row) => ({
          ...row,
          programMetrics: row.programMetrics.filter((metric) =>
            this.isRegionSelected(metric.region),
          ),
        }))
        .filter((row) => row.programMetrics.length > 0);
    }

    // Impact categories is a "soft" filter which only filters the programs based on whether they have at least one of the
    // selected impact categories in any region, but does not modify the underlying metrics so that the displayed totals and categories remain the same.
    if (this.hasSelectedImpactCategories()) {
      rows = rows.filter((row) =>
        row.programMetrics.some((metric) =>
          metric.impactCategories.some((category) =>
            this.isImpactCategorySelected(category),
          ),
        ),
      );
    }

    this.filteredProgramRows = rows;

    // Re‑aggregate and paginate after filtering
    this.tableRows = this.aggregateTvdByProgramData(this.filteredProgramRows);
    if (resetPage) {
      this.pageIndex = 0;
    }
    this.updatePagedRows();
  }

  /**
   * Initializes the available filter options for regions and impact categories based on the full set of program data retrieved from the API.
   * This should be called after any data load to populate the filter dropdowns with all possible values.
   * Subsequent calls to updateAvailableFilterOptions will dynamically adjust these options based on current selections to prevent invalid filter combinations.
   */
  private initializeFilterOptions(): void {
    this.availableRegions = [
      ...new Set(
        this._allProgramRows.flatMap((p) =>
          p.programMetrics.map((r) => r.region as Region),
        ),
      ),
    ];

    this.availableImpactCategories = [
      ...new Set(
        this._allProgramRows.flatMap((p) =>
          p.programMetrics.flatMap(
            (r) => r.impactCategories as TvdImpactCategory[],
          ),
        ),
      ),
    ];
  }

  /**
   * Dynamically updates the available filter options for regions and impact categories based on the current selections to ensure only valid combinations can be selected.
   * For example, if a region is selected, the available impact categories will be filtered to only those that exist within that region, and vice versa.
   * This method should be called whenever a filter selection changes to maintain the integrity of the filtering options.
   */
  private updateAvailableFilterOptions(): void {
    const metrics = this.getProgramSearchScopeMetrics();

    // ---- Region → Impact restriction
    this.availableImpactCategories = this.getAvailableImpactCategories(
      metrics,
      this.selectedRegions,
    );

    // ---- Impact → Region restriction
    this.availableRegions = this.getAvailableRegions(
      metrics,
      this.selectedImpactCategories,
    );

    // Prune invalid selections
    // Program/search is the driving filter and can invalidate downstream
    // region/impact selections.
    this.pruneInvalidSelections(
      this.availableRegions,
      this.availableImpactCategories,
    );

    // Recompute after pruning so options reflect the stabilized selections.
    // This prevents dependent dropdowns from staying empty when a stale
    // selection is auto-cleared.
    this.availableImpactCategories = this.getAvailableImpactCategories(
      metrics,
      this.selectedRegions,
    );
    this.availableRegions = this.getAvailableRegions(
      metrics,
      this.selectedImpactCategories,
    );
  }

  private getProgramSearchScopeMetrics(): TvdProgramMetric[] {
    const term = this.programSearchTerm.trim().toLowerCase();
    const baseRows = term
      ? this._allProgramRows.filter((row) =>
          row.program.toLowerCase().includes(term),
        )
      : this._allProgramRows;

    return baseRows.flatMap((program) => program.programMetrics);
  }

  private getAvailableImpactCategories(
    metrics: TvdProgramMetric[],
    selectedRegions: Region[],
  ): TvdImpactCategory[] {
    const regionFiltered =
      selectedRegions.length === 0
        ? metrics
        : metrics.filter((metric) =>
            selectedRegions.includes(metric.region as Region),
          );

    return [
      ...new Set(
        regionFiltered.flatMap(
          (metric) => metric.impactCategories as TvdImpactCategory[],
        ),
      ),
    ];
  }

  private getAvailableRegions(
    metrics: TvdProgramMetric[],
    selectedImpactCategories: TvdImpactCategory[],
  ): Region[] {
    const impactFiltered =
      selectedImpactCategories.length === 0
        ? metrics
        : metrics.filter((metric) =>
            metric.impactCategories.some((category) =>
              selectedImpactCategories.includes(category as TvdImpactCategory),
            ),
          );

    return [
      ...new Set(impactFiltered.map((metric) => metric.region as Region)),
    ];
  }

  private pruneInvalidSelections(
    availableRegions: Region[],
    availableImpactCategories: TvdImpactCategory[],
  ): void {
    this.selectedRegions = this.selectedRegions.filter((region) =>
      availableRegions.includes(region),
    );

    this.selectedImpactCategories = this.selectedImpactCategories.filter(
      (category) => availableImpactCategories.includes(category),
    );
  }
  // #endregion

  // #region Pagination
  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePagedRows();
  }

  private updatePagedRows(): void {
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    this.pagedRows = this.tableRows.slice(start, end);
  }
  // #endregion

  // #region Helpers
  /**
   * If the user drills-in from Partnership Impact app, auto-scroll to the program table.
   * The scroll will be tagged to the paginator so that we effectively scroll to the end of the page.
   */
  private scrollIntoViewIfDrilledIn(): void {
    document
      .getElementById('tvd-program-table-paginator')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  onProgramSearchInput(): void {
    if (
      this.programSearchTerm.trim().length >= 3 ||
      this.programSearchTerm.trim().length === 0
    ) {
      this.applyAllFilters();
    }
  }

  onProgramSearchEnter(): void {
    this.applyAllFilters();
  }

  private parseImpactCategories(raw: string[] = []): TvdImpactCategory[] {
    return raw
      .map((k) => TvdImpactCategory[k as keyof typeof TvdImpactCategory])
      .filter(Boolean);
  }

  getDisplayValue(value: number): number {
    if (value == null) return 0;

    return Math.round(value);
  }
  // #endregion

  // #region Filter Handlers
  onRegionFilterChange(regions: Region[]): void {
    this.selectedRegions = regions;
    this.updateAvailableFilterOptions();
    this.applyAllFilters();
  }

  onImpactCategoryFilterChange(categories: TvdImpactCategory[]): void {
    this.selectedImpactCategories = categories;
    this.updateAvailableFilterOptions();
    this.applyAllFilters();
  }

  isRegionSelected(region: Region): boolean {
    return this.selectedRegions.includes(region);
  }

  isImpactCategorySelected(category: TvdImpactCategory): boolean {
    return this.selectedImpactCategories.includes(category);
  }

  hasSelectedRegions(): boolean {
    return this.selectedRegions.length > 0;
  }

  hasSelectedImpactCategories(): boolean {
    return this.selectedImpactCategories.length > 0;
  }

  getRegionDisplayName(region: Region): string {
    return regionDisplayNames[region] ?? region;
  }
  // #endregion

  // #region Row Actions
  toggleRowActions(program: string, event: Event): void {
    event.stopPropagation();
    this.openActionsProgram =
      this.openActionsProgram === program ? null : program;
  }

  closeRowActions(): void {
    this.openActionsProgram = null;
  }

  isRowActionsOpen(program: string): boolean {
    return this.openActionsProgram === program;
  }
  // #endregion
}
